import React, { useEffect, useState } from "react"
import Client from "../../models/client"
import useSWR from "swr"
import { fetchEmailNoticesPreference, updateEmailNoticesPreference } from "../api"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { BaseModal } from "./BaseModal"
import { Box, Button, Group, Loader, ScrollArea, Stack, TextInput } from "@mantine/core"
import { confirmDiscardChanges } from "../utils/confirm"

const URL_REGEX =
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
  onSaved?: () => void
}

interface FormValues {
  footer_text: string
  footer_img_url: string
}

const defaultValues: FormValues = {
  footer_text: '',
  footer_img_url: '',
}

const EmailNoticesPreferenceModal: React.FC<Props> = ({
  opened,
  onClose,
  client,
  onSaved,
}) => {
  const [submitting, setSubmitting] = useState(false)

  const { data, isLoading } = useSWR(
    opened ? `/admin/clients/${client.id}/email_notices_preference/form` : null,
    async () => {
      try {
        return await fetchEmailNoticesPreference(client.id)
      } catch {
        return { footer_text: '', footer_img_url: '' }
      }
    },
  )

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      footer_img_url: (v) =>
        v && !URL_REGEX.test(v) ? '有効なURLを入力してください' : null,
    },
  })

  useEffect(() => {
    if (!opened || !data) return
    form.setValues({
      footer_text: data.footer_text ?? '',
      footer_img_url: data.footer_img_url ?? '',
    })
    form.resetDirty()
  }, [opened, data])

  const handleClose = () => {
    if (
      form.isDirty() &&
      !confirmDiscardChanges()
    ) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    try {
      await updateEmailNoticesPreference(client.id, {
        footer_text: values.footer_text,
        footer_img_url: values.footer_img_url,
      })
      notifications.show({ message: 'フッター設定を保存しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (e: any) {
      console.error('フッター設定の保存に失敗しました', e.response?.status, e.response?.data)
      const d = e.response?.data
      notifications.show({
        message: d?.errors?.join('\n') ?? d?.error ?? '保存に失敗しました',
        color: 'red',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const showLoader = opened && (isLoading || !data)

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="フッター設定"
      width={500}
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      {showLoader ? (
        <Group justify="center" py="xl" style={{ width: '100%' }}>
          <Loader />
        </Group>
      ) : (
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
            <Stack gap="md" p="md">
              <TextInput
                label="フッターテキスト"
                {...form.getInputProps('footer_text')}
              />
              <TextInput
                label="フッター画像（ロゴ）URL"
                {...form.getInputProps('footer_img_url')}
              />
            </Stack>
          </ScrollArea>

          <Box
            p="md"
            style={{
              borderTop: '1px solid #0000001A',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Group justify="flex-end" gap={8}>
              <Button
                variant="outline"
                radius="xl"
                color="gray"
                onClick={handleClose}
                disabled={submitting}
              >
                キャンセル
              </Button>
              <Button
                variant="filled"
                radius="xl"
                type="submit"
                loading={submitting}
              >
                保存
              </Button>
            </Group>
          </Box>
        </form>
      )}
    </BaseModal>
  )
}

export default EmailNoticesPreferenceModal
